<script setup>
// Operator tickets of the chat (TOPS-213): the list, and one ticket with its
// messages, notes, status and assignee. Seen only by operators.
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import MessageApi from 'dashboard/api/inbox/message';
import OperatorTicketsAPI from 'dashboard/api/operatorTickets';
import OperatorTicketDetail from './OperatorTicketDetail.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const store = useStore();
const agents = useMapGetter('agents/getAgents');

const tickets = ref([]);
const showClosed = ref(false);
const opened = ref(null);
const isSaving = ref(false);

const shownTickets = computed(() =>
  tickets.value.filter(ticket =>
    showClosed.value ? ticket.status === 'closed' : ticket.status === 'open'
  )
);
const closedCount = computed(
  () => tickets.value.filter(ticket => ticket.status === 'closed').length
);
const tabs = computed(() => [
  {
    closed: false,
    label: t('CONVERSATION.OPERATOR_TICKETS.FILTER_OPEN'),
    count: tickets.value.length - closedCount.value,
  },
  {
    closed: true,
    label: t('CONVERSATION.OPERATOR_TICKETS.FILTER_CLOSED'),
    count: closedCount.value,
  },
]);

const formatDate = unix =>
  new Date(unix * 1000).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  });

const fail = error =>
  useAlert(
    error.response?.data?.error || t('CONVERSATION.OPERATOR_TICKETS.FAILED')
  );

const loadTickets = async () => {
  try {
    const { data } = await MessageApi.operatorTickets(props.conversationId);
    tickets.value = data.payload;
  } catch (error) {
    tickets.value = [];
  }
};

const openTicket = async ticket => {
  try {
    const { data } = await MessageApi.operatorTicket(
      props.conversationId,
      ticket.id
    );
    opened.value = data;
  } catch (error) {
    fail(error);
  }
};

// Whatever changed, the list and the open ticket show it at once.
const applyTicket = data => {
  opened.value = data;
  const index = tickets.value.findIndex(ticket => ticket.id === data.id);
  if (index >= 0) tickets.value.splice(index, 1, data);
};

const run = async request => {
  isSaving.value = true;
  try {
    const { data } = await request();
    applyTicket(data);
  } catch (error) {
    fail(error);
  } finally {
    isSaving.value = false;
  }
};

const update = changes =>
  run(() =>
    MessageApi.updateOperatorTicket(
      props.conversationId,
      opened.value.id,
      changes
    )
  );

const addNote = content =>
  run(() =>
    MessageApi.addOperatorTicketNote(
      props.conversationId,
      opened.value.id,
      content
    )
  );

const addParticipant = userId =>
  run(() => OperatorTicketsAPI.addParticipant(opened.value.id, userId));

const removeParticipant = userId =>
  run(() => OperatorTicketsAPI.removeParticipant(opened.value.id, userId));

const deleteNote = note =>
  run(() =>
    MessageApi.deleteOperatorTicketNote(
      props.conversationId,
      opened.value.id,
      note.id
    )
  );

const removeMessage = message =>
  run(() =>
    MessageApi.removeFromOperatorTicket(
      props.conversationId,
      opened.value.id,
      message.id
    )
  );

const goToMessage = message =>
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId: message.id });

const refresh = async () => {
  await loadTickets();
  if (opened.value) openTicket(opened.value);
};

watch(
  () => props.conversationId,
  () => {
    opened.value = null;
    loadTickets();
  }
);

onMounted(() => {
  loadTickets();
  if (!agents.value.length) store.dispatch('agents/get');
  emitter.on(BUS_EVENTS.OPERATOR_TICKETS_CHANGED, refresh);
});

onBeforeUnmount(() => {
  emitter.off(BUS_EVENTS.OPERATOR_TICKETS_CHANGED, refresh);
});
</script>

<template>
  <div class="flex flex-col gap-2 px-2 pb-2 text-sm">
    <template v-if="!opened">
      <div class="grid grid-cols-2 gap-1 rounded-lg bg-n-alpha-1 p-1">
        <button
          v-for="tab in tabs"
          :key="tab.closed"
          type="button"
          class="flex items-center justify-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors"
          :class="
            showClosed === tab.closed
              ? 'bg-n-solid-3 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="showClosed = tab.closed"
        >
          {{ tab.label }}
          <span
            class="rounded-full px-1.5 text-[11px] tabular-nums"
            :class="
              showClosed === tab.closed
                ? 'bg-n-brand/15 text-n-brand'
                : 'bg-n-alpha-2'
            "
          >
            {{ tab.count }}
          </span>
        </button>
      </div>
      <button
        v-for="ticket in shownTickets"
        :key="ticket.id"
        type="button"
        class="group flex flex-col gap-1.5 rounded-xl border border-n-weak bg-n-solid-2 px-3 py-2.5 text-left transition-colors hover:border-n-strong hover:bg-n-solid-3"
        @click="openTicket(ticket)"
      >
        <span class="flex items-start gap-2">
          <span
            class="mt-1.5 size-2 shrink-0 rounded-full"
            :class="ticket.status === 'open' ? 'bg-n-teal-9' : 'bg-n-slate-8'"
          />
          <span class="min-w-0 flex-1 font-medium text-n-slate-12 line-clamp-2">
            {{ ticket.title }}
          </span>
          <span
            class="i-lucide-chevron-right mt-0.5 size-4 shrink-0 text-n-slate-9 transition-transform group-hover:translate-x-0.5"
          />
        </span>
        <span
          class="flex flex-wrap items-center gap-x-3 gap-y-1 pl-4 text-xs text-n-slate-11"
        >
          <span class="flex items-center gap-1">
            <span class="i-lucide-message-square size-3.5" />
            {{ ticket.messages_count }}
          </span>
          <span class="flex items-center gap-1">
            <span class="i-lucide-clock size-3.5" />
            {{ formatDate(ticket.updated_at) }}
          </span>
          <span
            v-if="ticket.assignee_name"
            class="flex min-w-0 items-center gap-1"
          >
            <span class="i-lucide-user-round size-3.5 shrink-0" />
            <span class="truncate">{{ ticket.assignee_name }}</span>
          </span>
        </span>
      </button>
      <div
        v-if="!shownTickets.length"
        class="flex flex-col items-center gap-2 rounded-xl border border-dashed border-n-weak px-4 py-6 text-center"
      >
        <span class="i-lucide-ticket size-6 text-n-slate-9" />
        <p class="text-xs text-n-slate-11">
          {{
            showClosed
              ? $t('CONVERSATION.OPERATOR_TICKETS.NONE_CLOSED')
              : $t('CONVERSATION.OPERATOR_TICKETS.EMPTY')
          }}
        </p>
      </div>
    </template>

    <template v-else>
      <NextButton
        xs
        ghost
        slate
        icon="i-lucide-chevron-left"
        class="self-start"
        :label="$t('CONVERSATION.OPERATOR_TICKETS.BACK')"
        @click="opened = null"
      />
      <OperatorTicketDetail
        :ticket="opened"
        :is-saving="isSaving"
        @update="update"
        @add-note="addNote"
        @delete-note="deleteNote"
        @remove-message="removeMessage"
        @go-to-message="goToMessage"
        @add-participant="addParticipant"
        @remove-participant="removeParticipant"
      />
    </template>
  </div>
</template>
